import type {
  DuplicateCandidate,
  MergeChoice,
  MergePreview,
  AddVisitWork,
  Attachment,
  Billing,
  ClinicalNote,
  ClinicalNoteVersion,
  ConfirmUploadRequest,
  CreatePatientRequest,
  FlagKindName,
  FlagSeverityName,
  Invoice,
  Patient,
  PatientFileWithGlance,
  PatientFlag,
  PatientPage,
  PatientProcedure,
  PatientSort,
  PresignedUpload,
  PresignUploadRequest,
  ProcedureCode,
  ToothChart,
  ToothDetail,
  UpdatePatientRequest,
  VisitDetail,
  VisitSummary,
  WriteClinicalNote,
} from "@dentacore/shared-types";

import { api } from "./client";

/**
 * The patient endpoints, as functions.
 *
 * All clinic-scoped — patients belong to the clinic and not to a branch
 * (settled decision 4.2), so none of these send the branch header. That is why
 * `branchScoped` is absent rather than false: the default is what is wanted,
 * and setting it explicitly would suggest the choice is per-call.
 */

export interface PatientSearchParams {
  q?: string;
  limit?: number;
  cursor?: string | null;
  /** Omit for the existing default order — see `PatientSort` in shared-types. */
  sort?: PatientSort;
  /** List removed files instead of live ones. Admin only (QA #38). */
  removed?: boolean;
  /** Rows to skip, for numbered pages (PF4). Ignored by the server when a cursor is sent. */
  offset?: number;
}

export function searchPatients(
  params: PatientSearchParams,
  signal?: AbortSignal,
): Promise<PatientPage> {
  const query = new URLSearchParams();
  if (params.q !== undefined && params.q !== "") query.set("q", params.q);
  if (params.limit !== undefined) query.set("limit", String(params.limit));
  if (params.cursor !== undefined && params.cursor !== null) query.set("cursor", params.cursor);
  if (params.sort !== undefined) query.set("sort", params.sort);
  if (params.removed === true) query.set("removed", "true");
  if (params.offset !== undefined && params.offset > 0) query.set("offset", String(params.offset));

  const suffix = query.toString();
  return api<PatientPage>(`/patients${suffix === "" ? "" : `?${suffix}`}`, { signal });
}

export function getPatient(
  patientId: string,
  signal?: AbortSignal,
): Promise<PatientFileWithGlance> {
  return api<PatientFileWithGlance>(`/patients/${patientId}`, { signal });
}

/**
 * The insurer names this clinic has already recorded, for the field to offer.
 *
 * **The clinic's own answers, played back** — there is no `insurers` table and
 * no shipped list of insurance companies, because that would be invented clinic
 * data and wrong outside whichever country it was written for. The first
 * patient types the name; every one after picks it, which is what stops `Bupa`,
 * `BUPA` and `Bupa Global` becoming three insurers in one clinic.
 */
export function listInsurers(signal?: AbortSignal): Promise<string[]> {
  return api<string[]>("/patients/insurers", { signal });
}

/** Returns the bare record — no flags or glance figures yet, since none exist for a new file. */
export function createPatient(body: CreatePatientRequest): Promise<Patient> {
  return api<Patient>("/patients", { method: "POST", body });
}

export function updatePatient(
  patientId: string,
  body: UpdatePatientRequest,
): Promise<PatientFileWithGlance> {
  return api<PatientFileWithGlance>(`/patients/${patientId}`, { method: "PATCH", body });
}

/**
 * Remove a patient file, and put one back (QA #38).
 *
 * **Both admin-only, and both soft** — nothing clinical is destroyed (P2 Q3,
 * Q6). They were built at STORY-017 and no screen had ever called either, so a
 * file opened by mistake stayed in the clinic's list for good.
 *
 * They arrive together deliberately: a delete with no way back is a delete
 * whatever the column says, and `searchPatients({ removed: true })` is how the
 * file is found again.
 */
export function removePatient(patientId: string): Promise<void> {
  return api<void>(`/patients/${patientId}`, { method: "DELETE" });
}

export function restorePatient(patientId: string): Promise<PatientFileWithGlance> {
  return api<PatientFileWithGlance>(`/patients/${patientId}/restore`, { method: "POST" });
}

export function getToothChart(patientId: string, signal?: AbortSignal): Promise<ToothChart> {
  return api<ToothChart>(`/patients/${patientId}/teeth`, { signal });
}

/**
 * The *Baby teeth* switch (BT1–BT3). Returns the chart, since the switch is
 * what changes it; a choice made before the 13th birthday expires on it.
 */
export function setPrimaryTeeth(patientId: string, shown: boolean): Promise<ToothChart> {
  return api<ToothChart>(`/patients/${patientId}/teeth/primary-teeth`, {
    method: "PUT",
    body: { shown },
  });
}

export function getTooth(
  patientId: string,
  fdiNumber: number,
  signal?: AbortSignal,
): Promise<ToothDetail> {
  return api<ToothDetail>(`/patients/${patientId}/teeth/${fdiNumber}`, { signal });
}

/**
 * Every procedure ever charted for this patient, newest first.
 *
 * Ordered by when the work was *done* rather than when it was typed, so a visit
 * written up that evening sits where it happened.
 */
export function listPatientProcedures(
  patientId: string,
  signal?: AbortSignal,
): Promise<PatientProcedure[]> {
  return api<PatientProcedure[]>(`/patients/${patientId}/teeth/procedures`, { signal });
}

/**
 * `appointmentId` is what makes the work billable — `raiseForAppointment`
 * looks for tooth procedures by it, and charting without one produces a
 * clinical record and no invoice.
 */
export function recordToothProcedure(
  patientId: string,
  fdiNumber: number,
  body: {
    label: string;
    procedureCodeId?: string;
    surfaces?: string[];
    /** PLANNED records work still to do; absent is COMPLETED, work done now. */
    status?: "PLANNED" | "COMPLETED";
    appointmentId?: string;
  },
): Promise<ToothDetail> {
  return api<ToothDetail>(`/patients/${patientId}/teeth/${fdiNumber}/procedures`, {
    method: "POST",
    body,
  });
}

export function cancelToothProcedure(
  patientId: string,
  fdiNumber: number,
  procedureId: string,
): Promise<ToothDetail> {
  return api<ToothDetail>(`/patients/${patientId}/teeth/${fdiNumber}/procedures/${procedureId}`, {
    method: "DELETE",
  });
}

/** Planned work on a tooth, marked done now by whoever pressed it. */
export function completeToothProcedure(
  patientId: string,
  fdiNumber: number,
  procedureId: string,
): Promise<ToothDetail> {
  return api<ToothDetail>(
    `/patients/${patientId}/teeth/${fdiNumber}/procedures/${procedureId}/complete`,
    { method: "POST" },
  );
}

export function markToothRemoved(patientId: string, fdiNumber: number): Promise<ToothDetail> {
  return api<ToothDetail>(`/patients/${patientId}/teeth/${fdiNumber}/removed`, { method: "POST" });
}

export function markToothNormal(patientId: string, fdiNumber: number): Promise<ToothDetail> {
  return api<ToothDetail>(`/patients/${patientId}/teeth/${fdiNumber}/normal`, { method: "POST" });
}

/**
 * The active branch's own catalogue. **Branch-scoped**, unlike everything else
 * in this file — each branch owns its catalogue outright (ADR-0008).
 */
export function listProcedureCodes(signal?: AbortSignal): Promise<ProcedureCode[]> {
  return api<ProcedureCode[]>("/procedure-codes", { branchScoped: true, signal });
}

/**
 * Patient flags — the allergies and warnings the header, the calendar and
 * Home's *Watch today* all read.
 *
 * ── Why these arrive late (QA #37) ──────────────────────────────────────────
 *
 * The six endpoints behind them have existed and worked since STORY-019, and
 * **nothing in the web app had ever called them.** Flags reached the screen only
 * because the seed wrote them and the patient payload embeds them for reading,
 * so a clinic starting from an empty database had no way to record a penicillin
 * allergy at all — while the calendar was already drawing a critical-flag
 * treatment for one, and Home was already counting them.
 *
 * **Resolved flags are kept, never deleted**, which is why there is a `reopen`
 * here and no `delete`: "this was never true" and "this is no longer true" are
 * different facts about a patient, and the second is the one a flag records.
 */
export function listFlags(patientId: string, signal?: AbortSignal): Promise<PatientFlag[]> {
  return api<PatientFlag[]>(`/patients/${patientId}/flags`, { signal });
}

export function createFlag(
  patientId: string,
  body: { kind: FlagKindName; label: string; detail?: string; severity?: FlagSeverityName },
): Promise<PatientFlag> {
  return api<PatientFlag>(`/patients/${patientId}/flags`, { method: "POST", body });
}

export function updateFlag(
  patientId: string,
  flagId: string,
  body: { kind?: FlagKindName; label?: string; detail?: string; severity?: FlagSeverityName },
): Promise<PatientFlag> {
  return api<PatientFlag>(`/patients/${patientId}/flags/${flagId}`, { method: "PATCH", body });
}

export function resolveFlag(patientId: string, flagId: string): Promise<PatientFlag> {
  return api<PatientFlag>(`/patients/${patientId}/flags/${flagId}/resolve`, { method: "POST" });
}

export function reopenFlag(patientId: string, flagId: string): Promise<PatientFlag> {
  return api<PatientFlag>(`/patients/${patientId}/flags/${flagId}/reopen`, { method: "POST" });
}

/**
 * Clinical notes. **Append-only** — `reviseNote` writes a new version and
 * `removeNote` hides, so nothing here can overwrite or destroy a record.
 */
export function listNotes(
  patientId: string,
  signal?: AbortSignal,
): Promise<{ notes: ClinicalNote[] }> {
  return api<{ notes: ClinicalNote[] }>(`/patients/${patientId}/notes`, { signal });
}

export function getNoteRevisions(
  patientId: string,
  noteId: string,
  signal?: AbortSignal,
): Promise<{ versions: ClinicalNoteVersion[] }> {
  return api<{ versions: ClinicalNoteVersion[] }>(
    `/patients/${patientId}/notes/${noteId}/revisions`,
    { signal },
  );
}

export function writeNote(patientId: string, body: WriteClinicalNote): Promise<ClinicalNote> {
  return api<ClinicalNote>(`/patients/${patientId}/notes`, { method: "POST", body });
}

export function reviseNote(
  patientId: string,
  noteId: string,
  body: WriteClinicalNote,
): Promise<ClinicalNote> {
  return api<ClinicalNote>(`/patients/${patientId}/notes/${noteId}`, { method: "PATCH", body });
}

export function removeNote(patientId: string, noteId: string): Promise<ClinicalNote> {
  return api<ClinicalNote>(`/patients/${patientId}/notes/${noteId}`, { method: "DELETE" });
}

/**
 * Attachments. **No file passes through these** — `presignAttachment` asks for
 * somewhere to put one, the browser uploads to storage directly, and
 * `confirmAttachment` records that it worked. See `upload-file.ts`.
 */
export function listAttachments(
  patientId: string,
  signal?: AbortSignal,
): Promise<{ attachments: Attachment[] }> {
  return api<{ attachments: Attachment[] }>(`/patients/${patientId}/attachments`, { signal });
}

export function presignAttachment(
  patientId: string,
  body: PresignUploadRequest,
): Promise<PresignedUpload> {
  return api<PresignedUpload>(`/patients/${patientId}/attachments/presign`, {
    method: "POST",
    body,
  });
}

export function confirmAttachment(
  patientId: string,
  body: ConfirmUploadRequest,
): Promise<Attachment> {
  return api<Attachment>(`/patients/${patientId}/attachments`, { method: "POST", body });
}

export function removeAttachment(patientId: string, attachmentId: string): Promise<void> {
  return api<void>(`/patients/${patientId}/attachments/${attachmentId}`, { method: "DELETE" });
}

/**
 * Billing. **Records, not payments** — nothing here moves money, and there is
 * no card, gateway or token anywhere in the path.
 */
export function getBilling(patientId: string, signal?: AbortSignal): Promise<Billing> {
  return api<Billing>(`/patients/${patientId}/invoices`, { signal });
}

export function createInvoice(
  patientId: string,
  body: { branchId: string; notes?: string; appointmentId?: string },
): Promise<Invoice> {
  return api<Invoice>(`/patients/${patientId}/invoices`, { method: "POST", body });
}

export function addInvoiceLine(
  patientId: string,
  invoiceId: string,
  body: {
    procedureCodeId?: string;
    description?: string;
    fdiNumber?: number;
    quantity?: number;
    unitPriceCents?: number;
    overrideNote?: string;
  },
): Promise<Invoice> {
  return api<Invoice>(`/patients/${patientId}/invoices/${invoiceId}/lines`, {
    method: "POST",
    body,
  });
}

export function issueInvoice(patientId: string, invoiceId: string): Promise<Invoice> {
  return api<Invoice>(`/patients/${patientId}/invoices/${invoiceId}/issue`, { method: "POST" });
}

/** Void an issued invoice, with a reason that is audited (OQ-D4). */
export function voidInvoice(
  patientId: string,
  invoiceId: string,
  reason: string,
): Promise<Invoice> {
  return api<Invoice>(`/patients/${patientId}/invoices/${invoiceId}/void`, {
    method: "POST",
    body: { reason },
  });
}

export function recordPayment(
  patientId: string,
  invoiceId: string,
  body: { amountCents: number; method?: string; reference?: string },
): Promise<Invoice> {
  return api<Invoice>(`/patients/${patientId}/invoices/${invoiceId}/payments`, {
    method: "POST",
    body,
  });
}

/**
 * Visits. **Addressed by local date, not by appointment id** — the records that
 * make up a visit reach an appointment only optionally.
 */
export function listVisits(
  patientId: string,
  signal?: AbortSignal,
): Promise<{ visits: VisitSummary[] }> {
  return api<{ visits: VisitSummary[] }>(`/patients/${patientId}/visits`, { signal });
}

export function getVisit(
  patientId: string,
  date: string,
  signal?: AbortSignal,
): Promise<VisitDetail> {
  return api<VisitDetail>(`/patients/${patientId}/visits/${date}`, { signal });
}

/**
 * Finish a visit and bill what was done at it (P2 Q15).
 *
 * **Branch-scoped**, unlike the rest of this file: an appointment happens in a
 * chair, a chair belongs to a branch.
 */
/**
 * Record more work on a completed visit and bill it, in one action (EB7). The
 * visit's own invoice is untouched; this answers the new one.
 */
export function addVisitWork(patientId: string, body: AddVisitWork): Promise<Invoice> {
  return api<Invoice>(`/patients/${patientId}/invoices/visit-work`, { method: "POST", body });
}

export function completeVisit(
  appointmentId: string,
): Promise<{ id: string; status: string; invoice: Invoice | null }> {
  return api<{ id: string; status: string; invoice: Invoice | null }>(
    `/appointments/${appointmentId}/complete`,
    { method: "POST", branchScoped: true },
  );
}

/** Files a new one might duplicate: same date of birth, a close name (PP-D14). */
export function possibleDuplicates(
  person: { firstName: string; lastName: string; dateOfBirth: string; excludeId?: string },
  signal?: AbortSignal,
): Promise<DuplicateCandidate[]> {
  const query = new URLSearchParams({
    firstName: person.firstName,
    lastName: person.lastName,
    dateOfBirth: person.dateOfBirth,
  });
  if (person.excludeId !== undefined) query.set("excludeId", person.excludeId);
  return api<DuplicateCandidate[]>(`/patients/possible-duplicates?${query.toString()}`, { signal });
}

/** Every pair of files that might be one person — administrators (PP-D14). */
export function duplicatePairs(
  signal?: AbortSignal,
): Promise<{ a: DuplicateCandidate; b: DuplicateCandidate }[]> {
  return api(`/patients/duplicates`, { signal });
}

/** What merging `otherId` into `keepId` would do (PP-D13). */
export function mergePreview(keepId: string, otherId: string): Promise<MergePreview> {
  return api<MergePreview>(`/patients/${keepId}/merge/${otherId}`);
}

/** Merge `otherId` into `keepId` — one way (PP-D13). */
export function mergePatients(
  keepId: string,
  otherId: string,
  choice: MergeChoice,
): Promise<{ keptId: string }> {
  return api(`/patients/${keepId}/merge/${otherId}`, { method: "POST", body: choice });
}

/** Erase a patient's identity on request, keeping their record — administrators (PP-D16). */
export function erasePatient(patientId: string): Promise<PatientFileWithGlance> {
  return api<PatientFileWithGlance>(`/patients/${patientId}/erase`, { method: "POST", body: {} });
}
